<script setup>
import { computed, ref } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Analise de Empresas',
  },
});

const activeView = ref('overview');

function formatCurrency(value) {
  if (!value) return 'R$ 0';
  if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}K`;
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

const topCompanies = computed(() => props.data?.top_by_value || []);
const byIndustry = computed(() => props.data?.by_industry || []);

function getInitials(name) {
  if (!name) return '??';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-building-2 text-lg text-n-cyan-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex gap-1">
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeView === 'overview'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeView = 'overview'"
        >
          Visao Geral
        </button>
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeView === 'top'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeView = 'top'"
        >
          Top Empresas
        </button>
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeView === 'industry'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeView = 'industry'"
        >
          Por Setor
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Overview View -->
    <div v-else-if="activeView === 'overview'" class="flex flex-col gap-4">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Total Empresas</div>
          <div class="text-xl font-bold text-n-slate-12">
            {{ formatNumber(data.total_companies) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Com Negocios</div>
          <div class="text-xl font-bold text-n-blue-11">
            {{ formatNumber(data.with_deals) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Total Negocios</div>
          <div class="text-xl font-bold text-n-violet-11">
            {{ formatNumber(data.total_deals) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Valor Total</div>
          <div class="text-xl font-bold text-n-teal-11">
            {{ formatCurrency(data.total_value) }}
          </div>
        </div>
      </div>

      <div class="grid grid-cols-3 gap-3">
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Ticket Medio</div>
          <div class="text-lg font-bold text-n-violet-11">
            {{ formatCurrency(data.avg_deal_value) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Negocios/Empresa</div>
          <div class="text-lg font-bold text-n-blue-11">
            {{ (data.avg_deals_per_company || 0).toFixed(1) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Total Contatos</div>
          <div class="text-lg font-bold text-n-slate-12">
            {{ formatNumber(data.total_contacts) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Top Companies View -->
    <div v-else-if="activeView === 'top'" class="flex flex-col gap-2">
      <div
        v-if="topCompanies.length === 0"
        class="text-center text-n-slate-11 py-8"
      >
        Nenhuma empresa encontrada
      </div>
      <div
        v-for="(company, idx) in topCompanies"
        :key="company.id"
        class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="flex items-center gap-3">
          <div class="relative">
            <div
              class="w-10 h-10 rounded-lg bg-n-cyan-3 flex items-center justify-center text-n-cyan-11 font-medium text-sm"
            >
              {{ getInitials(company.name) }}
            </div>
            <span
              v-if="idx < 3"
              class="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold"
              :class="{
                'bg-n-amber-9 text-white': idx === 0,
                'bg-n-slate-9 text-white': idx === 1,
                'bg-n-orange-9 text-white': idx === 2,
              }"
            >
              {{ idx + 1 }}
            </span>
          </div>
          <div class="flex flex-col">
            <span class="font-medium text-n-slate-12">{{ company.name }}</span>
            <span class="text-xs text-n-slate-11">
              {{ company.industry || 'Sem setor' }} •
              {{ formatNumber(company.deals_count) }} negocios
            </span>
          </div>
        </div>
        <div class="flex flex-col items-end">
          <span class="text-lg font-bold text-n-teal-11">{{
            formatCurrency(company.total_value)
          }}</span>
          <span class="text-xs text-n-slate-10">{{ formatNumber(company.contacts_count) }} contatos</span>
        </div>
      </div>
    </div>

    <!-- By Industry View -->
    <div v-else-if="activeView === 'industry'" class="flex flex-col gap-3">
      <div
        v-if="byIndustry.length === 0"
        class="text-center text-n-slate-11 py-8"
      >
        Nenhum setor registrado
      </div>
      <div
        v-for="industry in byIndustry"
        :key="industry.industry"
        class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="flex items-center gap-3">
          <span class="i-lucide-building text-n-slate-10" />
          <div class="flex flex-col">
            <span class="font-medium text-n-slate-12">{{
              industry.industry || 'Nao especificado'
            }}</span>
            <span class="text-xs text-n-slate-11">{{ formatNumber(industry.companies_count) }} empresas</span>
          </div>
        </div>
        <div class="flex flex-col items-end">
          <span class="text-sm font-bold text-n-teal-11">{{
            formatCurrency(industry.total_value)
          }}</span>
          <span class="text-xs text-n-slate-10">{{ formatNumber(industry.deals_count) }} negocios</span>
        </div>
      </div>
    </div>
  </div>
</template>
